/**
 * Placeholder sidebar panel for the DOCX-native template editor.
 *
 * Reuses the ORIGINAL placeholder-sidebar design (PlaceholderItem + its SCSS)
 * so it looks identical to the CKEditor sidebar — but reads placeholders live
 * from the editor via the docx-native registry (not the CKEditor-era backend
 * completeness query). Clicking an item scrolls to + selects the placeholder;
 * the "+" adds a placeholder at the cursor.
 */

import React, { useEffect, useState } from 'react';
import PlaceholderItem from '../components/placeholderSidebar/placeholderItem';
import styles from '../components/placeholderSidebar/styles.module.scss';
import {
  getDocxNativeEditor,
  onRegistryChange,
  type DocxNativePlaceholder,
} from './docxNativeEditorRegistry';
import { requestPlaceholderPopupOpen } from './PlaceholderPopup';

export function DocxNativePlaceholderPanel({ documentId }: { documentId: string }) {
  const [placeholders, setPlaceholders] = useState<DocxNativePlaceholder[]>([]);
  const [activePos, setActivePos] = useState<number | null>(null);

  useEffect(() => {
    let unsubChange: (() => void) | undefined;
    const refresh = () => {
      const handle = getDocxNativeEditor(documentId);
      setPlaceholders(handle ? handle.listPlaceholders() : []);
    };
    const attach = () => {
      unsubChange?.();
      const handle = getDocxNativeEditor(documentId);
      unsubChange = handle?.subscribe(refresh);
      refresh();
    };
    attach();
    const unsubRegistry = onRegistryChange((id) => {
      if (id === documentId) attach();
    });
    // Editor may register a beat after this panel mounts.
    const t = window.setInterval(refresh, 1000);
    return () => {
      unsubChange?.();
      unsubRegistry();
      window.clearInterval(t);
    };
  }, [documentId]);

  // PlaceholderItem calls onClick(uuid); we key uuid = the placeholder's PM pos.
  // Scroll to + select the placeholder, THEN open the edit popup on it — the
  // popup reads the current selection (which focusPlaceholder just set inside
  // the SDT), so a sidebar click edits the field, not just navigates to it.
  const onItemClick = (uuid: string) => {
    const pos = Number(uuid);
    setActivePos(pos);
    getDocxNativeEditor(documentId)?.focusPlaceholder(pos);
    // Defer one tick so the selection dispatch settles before the popup polls
    // the selection to resolve which placeholder to edit.
    window.setTimeout(() => requestPlaceholderPopupOpen(), 0);
  };

  const onAdd = () => {
    getDocxNativeEditor(documentId)?.insertPlaceholderAtCursor();
  };

  return (
    <div className={styles.wrapper}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Placeholders</h2>
        <button
          type="button"
          onClick={onAdd}
          title="Add a placeholder at the cursor"
          aria-label="Add placeholder"
          style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            padding: 0,
            width: 28,
            height: 28,
            backgroundColor: '#006c90',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#005a7a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#006c90';
          }}
        >
          +
        </button>
      </div>
      {placeholders.length === 0 ? (
        <div className={styles.noPlaceholders}>No placeholders found.</div>
      ) : (
        placeholders.map((ph, i) => (
          <PlaceholderItem
            key={`${ph.pos}-${i}`}
            id={String(ph.pos)}
            uuid={String(ph.pos)}
            title={ph.name}
            isRequired={ph.required}
            isActive={ph.pos === activePos}
            onClick={onItemClick}
          />
        ))
      )}
    </div>
  );
}
